import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Alert,
  styled,
  Card,
  CardContent,
  alpha,
  Skeleton,
  Button,
  Stack,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import DescriptionIcon from '@mui/icons-material/Description';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import SecurityIcon from '@mui/icons-material/Security';
import BugReportIcon from '@mui/icons-material/BugReport';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SEOHead from '../components/SEOHead';
import { cyber } from '../theme';

// --- Types (mirror of PublicStatistics DTO) ---
interface NameValue {
  name: string;
  value: number;
}

interface PublicStatistics {
  total_reports: number;
  reports_last_week: number;
  total_unique_sources: number;
  top_report_sources: { source: string; count: number }[];
  top_tags: NameValue[];
  total_iocs: number;
  iocs_last_7_days: number;
  ioc_type_distribution: NameValue[];
  total_cves: number;
  cves_last_7_days: number;
  cve_severity_distribution: NameValue[];
  avg_cvss_score: number | null;
  exploited_in_wild_count: number;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const PIE_COLORS = [cyber.primary, cyber.accent.cyan, cyber.accent.green, cyber.accent.coral, '#ffab40', '#ab47bc'];

const StatsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(4),
  gridTemplateColumns: 'repeat(1, 1fr)',
  [theme.breakpoints.up('sm')]: { gridTemplateColumns: 'repeat(2, 1fr)' },
  [theme.breakpoints.up('md')]: { gridTemplateColumns: 'repeat(4, 1fr)' },
}));

const ChartGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(3),
  gridTemplateColumns: '1fr',
  [theme.breakpoints.up('lg')]: { gridTemplateColumns: 'repeat(2, 1fr)' },
}));

const GradientCard = styled(Card)(() => ({
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: cyber.gradient,
  },
}));

const KpiCard: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode; sub?: string }> = ({
  icon, label, value, sub,
}) => (
  <GradientCard>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, color: 'primary.main' }}>
        {icon}
        <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
      </Box>
      <Typography variant="h4" fontWeight="bold">{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </CardContent>
  </GradientCard>
);

const PublicStatisticsPage: React.FC = () => {
  const [stats, setStats] = useState<PublicStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/public/statistics`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json: PublicStatistics = await res.json();
        if (!cancelled) setStats(json);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load statistics');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <SEOHead
        title="Threat Intelligence Statistics"
        description="Live platform statistics: processed threat intelligence reports, sources, extracted IOCs and CVE coverage. Explore the TI Mindmap HUB dataset at a glance."
        canonical="/stats"
        type="website"
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ ...cyber.gradientText }}>
            Platform Statistics
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 720, mx: 'auto' }}>
            A live snapshot of the threat intelligence processed by TI Mindmap HUB.
          </Typography>
        </Box>

        {error && !loading && (
          <Alert severity="error" sx={{ mb: 3 }}>Unable to load statistics: {error}</Alert>
        )}

        {loading && (
          <StatsGrid>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={120} />
            ))}
          </StatsGrid>
        )}

        {!loading && !error && stats && (
          <>
            <StatsGrid>
              <KpiCard
                icon={<DescriptionIcon />}
                label="Reports"
                value={stats.total_reports.toLocaleString()}
                sub={`${stats.reports_last_week} in the last 7 days`}
              />
              <KpiCard
                icon={<RssFeedIcon />}
                label="Sources"
                value={stats.total_unique_sources.toLocaleString()}
              />
              <KpiCard
                icon={<SecurityIcon />}
                label="IOCs"
                value={stats.total_iocs.toLocaleString()}
                sub={`${stats.iocs_last_7_days} in the last 7 days`}
              />
              <KpiCard
                icon={<BugReportIcon />}
                label="CVEs"
                value={stats.total_cves.toLocaleString()}
                sub={stats.avg_cvss_score != null ? `Avg CVSS ${stats.avg_cvss_score.toFixed(1)}` : undefined}
              />
            </StatsGrid>

            <ChartGrid>
              {stats.top_tags && stats.top_tags.length > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>Top Tags</Typography>
                    <ResponsiveContainer width="100%" height={320}>
                      <BarChart data={stats.top_tags} layout="vertical" margin={{ left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha('#fff', 0.08)} />
                        <XAxis type="number" stroke="#aaa" />
                        <YAxis type="category" dataKey="name" width={120} stroke="#aaa" />
                        <Tooltip contentStyle={{ background: cyber.bgPaper, border: `1px solid ${cyber.border}` }} />
                        <Bar dataKey="value" fill={cyber.primary} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {stats.ioc_type_distribution && stats.ioc_type_distribution.length > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>IOC Type Distribution</Typography>
                    <ResponsiveContainer width="100%" height={320}>
                      <PieChart>
                        <Pie
                          data={stats.ioc_type_distribution}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={110}
                          label
                        >
                          {stats.ioc_type_distribution.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ background: cyber.bgPaper, border: `1px solid ${cyber.border}` }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {stats.cve_severity_distribution && stats.cve_severity_distribution.length > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>CVE Severity Distribution</Typography>
                    <ResponsiveContainer width="100%" height={320}>
                      <BarChart data={stats.cve_severity_distribution}>
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha('#fff', 0.08)} />
                        <XAxis dataKey="name" stroke="#aaa" />
                        <YAxis stroke="#aaa" />
                        <Tooltip contentStyle={{ background: cyber.bgPaper, border: `1px solid ${cyber.border}` }} />
                        <Bar dataKey="value" fill={cyber.accent.coral} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {stats.top_report_sources && stats.top_report_sources.length > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>Top Sources</Typography>
                    <ResponsiveContainer width="100%" height={320}>
                      <BarChart data={stats.top_report_sources} layout="vertical" margin={{ left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha('#fff', 0.08)} />
                        <XAxis type="number" stroke="#aaa" />
                        <YAxis type="category" dataKey="source" width={140} stroke="#aaa" />
                        <Tooltip contentStyle={{ background: cyber.bgPaper, border: `1px solid ${cyber.border}` }} />
                        <Bar dataKey="count" fill={cyber.accent.cyan} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </ChartGrid>
          </>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 6 }}>
          <Button component={RouterLink} to="/reports" variant="outlined" size="large" endIcon={<ArrowForwardIcon />}>
            Browse reports
          </Button>
          <Button component={RouterLink} to="/" variant="contained" size="large" endIcon={<ArrowForwardIcon />}>
            Sign in to access the full platform
          </Button>
        </Stack>
      </Container>
    </>
  );
};

export default PublicStatisticsPage;
